import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { GraduationCap, Bus, ShoppingBag, TreePine, UtensilsCrossed, Heart, MapPin, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  schools: { icon: GraduationCap, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  transit: { icon: Bus, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  shopping: { icon: ShoppingBag, color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200' },
  parks: { icon: TreePine, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' },
  restaurants: { icon: UtensilsCrossed, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
  healthcare: { icon: Heart, color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200' },
};

function ScoreRing({ score }) {
  const circumference = 2 * Math.PI * 42;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 75 ? '#16a34a' : score >= 50 ? '#d97706' : score >= 25 ? '#ea580c' : '#dc2626';

  return (
    <div className="relative w-28 h-28 mx-auto">
      <svg className="w-28 h-28 -rotate-90" viewBox="0 0 96 96">
        <circle cx="48" cy="48" r="42" fill="none" stroke="#e2e8f0" strokeWidth="6" />
        <circle
          cx="48" cy="48" r="42" fill="none"
          stroke={color} strokeWidth="6" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1s ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-black" style={{ color }}>{score}</span>
        <span className="text-[10px] text-slate-500 font-medium uppercase tracking-wide">/ 100</span>
      </div>
    </div>
  );
}

function CategoryCard({ categoryKey, data, t }) {
  const config = CATEGORY_CONFIG[categoryKey];
  const Icon = config.icon;
  const [expanded, setExpanded] = useState(false);

  return (
    <div data-testid={`neighborhood-${categoryKey}`} className={`rounded-2xl border ${config.border} ${config.bg} p-3`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${config.color}`} />
          <span className="text-sm font-semibold text-slate-800">{t(`neighborhood.${categoryKey}`)}</span>
        </div>
        <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${config.bg} ${config.color}`}>
          {data.count}
        </span>
      </div>
      {data.items.length > 0 && (
        <>
          <div className="text-xs text-slate-600 mt-1">
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
              <span className="truncate">{data.items[0].name}</span>
              <span className="text-slate-400 flex-shrink-0">{data.items[0].distance}m</span>
            </div>
          </div>
          {data.items.length > 1 && (
            <>
              {expanded && data.items.slice(1).map((item, i) => (
                <div key={i} className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-slate-300 flex-shrink-0" />
                  <span className="truncate">{item.name}</span>
                  <span className="text-slate-400 flex-shrink-0">{item.distance}m</span>
                </div>
              ))}
              <button
                onClick={() => setExpanded(!expanded)}
                className={`text-xs mt-1 font-medium ${config.color} hover:underline`}
              >
                {expanded ? t('neighborhood.showLess') : t('neighborhood.showMore', { count: data.items.length - 1 })}
              </button>
            </>
          )}
        </>
      )}
      {data.items.length === 0 && (
        <p className="text-xs text-slate-400 mt-1">{t('neighborhood.noneFound')}</p>
      )}
    </div>
  );
}

export default function NeighborhoodInsights({ latitude, longitude, preloadedData }) {
  const { t } = useTranslation();
  const [data, setData] = useState(preloadedData || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (preloadedData) {
      setData(preloadedData);
      return;
    }
    if (!latitude || !longitude) return;

    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(false);
      try {
        const resp = await axios.post(`${BACKEND_URL}/api/property/neighborhood`, {
          latitude, longitude, radius: 1000
        }, { withCredentials: true });
        if (!cancelled) setData(resp.data);
      } catch (e) {
        console.error('Neighborhood fetch failed:', e);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [latitude, longitude, preloadedData]);

  if (!latitude || !longitude) return null;

  if (loading) {
    return (
      <div data-testid="neighborhood-loading" className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center justify-center gap-2 text-slate-500 py-8">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">{t('neighborhood.loading')}</span>
        </div>
      </div>
    );
  }

  if (error || !data) return null;

  return (
    <div data-testid="neighborhood-insights" className="bg-white border border-slate-200 rounded-2xl p-6">
      <h3 className="font-primary font-bold text-lg text-slate-900 mb-4">{t('neighborhood.title')}</h3>

      {/* Location Score */}
      <div className="text-center mb-5">
        <ScoreRing score={data.location_score} />
        <p className="text-sm text-slate-600 mt-2 font-medium">{t('neighborhood.locationScore')}</p>
        <p className="text-xs text-slate-400">{t('neighborhood.withinRadius')}</p>
      </div>

      {/* Category Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {Object.keys(CATEGORY_CONFIG).map(key => (
          <CategoryCard key={key} categoryKey={key} data={data[key]} t={t} />
        ))}
      </div>

      <p className="text-[10px] text-slate-400 mt-3 text-center">{t('neighborhood.dataSource')}</p>
    </div>
  );
}
